// src/hooks/useInsuranceProviders.ts

import { useCallback } from "react";
import { toast } from "sonner";

import type {
  ActivityLog,
  Provider,
} from "../contexts/InsuranceContextType";

interface UseInsuranceProvidersOptions {
  setProviders: React.Dispatch<
    React.SetStateAction<Provider[]>
  >;

  logActivity: (
    activity: Omit<
      ActivityLog,
      "id" | "timestamp"
    >
  ) => void;
}

interface UseInsuranceProvidersReturn {
  addProvider: (
    providerData: Omit<
      Provider,
      "id" | "createdAt" | "updatedAt"
    >
  ) => Promise<void>;

  updateProvider: (
    id: string,
    updates: Partial<Provider>
  ) => Promise<void>;

  suspendProvider: (
    id: string,
    reason: string
  ) => Promise<void>;

  activateProvider: (
    id: string
  ) => Promise<void>;
}

const createProviderId = (): string =>
  `PRV-${Date.now()}`;

export function useInsuranceProviders({
  setProviders,
  logActivity,
}: UseInsuranceProvidersOptions): UseInsuranceProvidersReturn {
  const addProvider = useCallback(
    async (
      providerData: Omit<
        Provider,
        "id" | "createdAt" | "updatedAt"
      >
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setProviders]
  );

  const updateProvider = useCallback(
    async (
      id: string,
      updates: Partial<Provider>
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [setProviders]
  );

  const suspendProvider = useCallback(
    async (
      id: string,
      reason: string
    ) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setProviders]
  );

  const activateProvider = useCallback(
    async (id: string) => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [logActivity, setProviders]
  );

  return {
    addProvider,
    updateProvider,
    suspendProvider,
    activateProvider,
  };
}